import React from 'react';
import { Link } from 'react-router-dom';

const Glossary = () => {
const terms = [
    // New Trading Principles Terms
    {
      term: "Chart Pattern",
      definition: "A recognizable pattern that forms on a price chart, such as head and shoulders, double tops/bottoms, or cup and handle, used to predict future price movements."
    },
    {
      term: "Trading Journal",
      definition: "A systematic record of trades that allows traders to analyze performance, identify patterns in trading behavior, and continuously improve strategies."
    },
    {
      term: "Bollinger Bands",
      definition: "A technical analysis tool that creates a price channel around the simple moving average, with the width of the channel based on standard deviation."
    },
    {
      term: "Advance-Decline Line",
      definition: "A technical indicator that plots the cumulative sum of advancing stocks minus declining stocks, used to measure market breadth and confirm price trends."
    },
    {
      term: "ATR (Average True Range)",
      definition: "A technical indicator that measures market volatility by decomposing the entire range of an asset price for a specific period."
    },
    
    // New Risk Management Terms
    {
      term: "Sharpe Ratio",
      definition: "A measure of risk-adjusted return that compares an investment's excess return to its standard deviation of returns, helping evaluate performance relative to risk taken."
    },
    {
      term: "Sortino Ratio",
      definition: "A variation of the Sharpe ratio that only considers downside risk (negative returns) rather than total volatility, focusing on harmful volatility."
    },
    {
      term: "Maximum Drawdown",
      definition: "The largest peak-to-trough decline in the value of an investment or portfolio, used to assess downside risk and recovery potential."
    },
    {
      term: "Tail Risk",
      definition: "The risk of extreme, unexpected events that fall outside normal distribution patterns—the 'fat tails' of the probability curve."
    },
    {
      term: "Volatility Targeting",
      definition: "A risk management strategy that adjusts portfolio allocations to maintain a consistent level of volatility over time."
    },
    
    // New Market Analysis Terms
    {
      term: "Intermarket Analysis",
      definition: "The study of relationships between different asset classes (stocks, bonds, commodities, currencies) to gain insights into potential market movements."
    },
    {
      term: "Market Breadth",
      definition: "A measure of the number of stocks participating in a market move, used to assess the underlying strength or weakness of market trends."
    },
    {
      term: "Fund Flows",
      definition: "The net movement of money into or out of investment funds, used to track investor sentiment and potential market trends."
    },
    {
      term: "Short Interest",
      definition: "The total number of shares of a particular stock that have been sold short but not yet covered or closed, indicating negative sentiment."
    },
    {
      term: "Global Macro",
      definition: "An investment strategy that bases its holdings on broad economic and political views of individual countries and global trends."
    },
    // Trading Platforms
    {
      term: "Commission-Free Trading",
      definition: "A pricing model where brokers don't charge a fee for executing buy and sell orders for stocks, ETFs, and sometimes options."
    },
    {
      term: "Fractional Shares",
      definition: "The ability to buy a portion of a share of stock, allowing investors to invest in high-priced stocks with smaller amounts of money."
    },
    {
      term: "Robo-Advisor",
      definition: "An automated investment platform that uses algorithms to create and manage a diversified portfolio based on your risk tolerance and goals."
    },
    {
      term: "Payment for Order Flow",
      definition: "A practice where brokers receive compensation for directing client orders to specific market makers for execution."
    },
    {
      term: "Extended Trading Hours",
      definition: "Trading sessions that occur outside of regular market hours (9:30 AM to 4:00 PM ET), including pre-market and after-hours trading."
    },
    {
      term: "Paper Trading",
      definition: "A simulated trading practice that allows investors to test trading strategies without risking real money."
    },
    {
      term: "Margin Trading",
      definition: "Trading with funds borrowed from a broker, allowing investors to leverage their positions but increasing potential risk."
    },
    {
      term: "Options Contract",
      definition: "A financial derivative that gives the buyer the right, but not the obligation, to buy or sell an asset at a specified price within a specific time period."
    },
    {
      term: "Broker-Assisted Trade",
      definition: "A trade executed with the help of a human broker, typically involving a higher fee than self-directed online trades."
    },
    {
      term: "API Access",
      definition: "Application Programming Interface access that allows traders to connect their own software to a brokerage platform for automated or algorithmic trading."
    },
    
    // Stock Market Fundamentals
    {
      term: "Stock",
      definition: "A share of ownership in a company that represents a claim on part of the company's assets and earnings."
    },
    {
      term: "Dividend",
      definition: "A payment made by a corporation to its shareholders, usually as a distribution of profits."
    },
    {
      term: "Market Cap",
      definition: "The total value of a company's shares of stock, calculated by multiplying the stock price by the number of shares outstanding."
    },
    {
      term: "P/E Ratio",
      definition: "Price-to-Earnings ratio, a valuation metric that compares a company's current share price to its per-share earnings."
    },
    {
      term: "Bull Market",
      definition: "A market condition in which prices are rising or are expected to rise."
    },
    {
      term: "Bear Market",
      definition: "A market condition in which prices are falling or are expected to fall."
    },
    {
      term: "IPO",
      definition: "Initial Public Offering - the process through which a private company offers shares to the public for the first time."
    },
    {
      term: "Stock Exchange",
      definition: "An organized marketplace where stocks and other securities are traded, such as the NYSE or NASDAQ."
    },
    {
      term: "Market Index",
      definition: "A statistical measure that tracks the performance of a group of stocks, such as the S&P 500 or Dow Jones Industrial Average."
    },
    {
      term: "Large-Cap",
      definition: "Companies with a market capitalization over $10 billion, typically characterized by stable growth and often paying dividends."
    },
    {
      term: "Mid-Cap",
      definition: "Companies with a market capitalization between $2-10 billion, often in a growth phase with moderate risk profiles."
    },
    {
      term: "Small-Cap",
      definition: "Companies with a market capitalization between $300 million-$2 billion, typically with higher growth potential but more volatility."
    },
    
    // Investment Basics
    {
      term: "Asset Allocation",
      definition: "The strategy of dividing investments among different asset classes such as stocks, bonds, and cash to balance risk and reward."
    },
    {
      term: "Diversification",
      definition: "Spreading investments across various assets to reduce risk and minimize the impact of any single investment's performance."
    },
    {
      term: "ETF",
      definition: "Exchange-Traded Fund - a basket of securities that trades like an individual stock on an exchange, offering diversification and typically lower fees."
    },
    {
      term: "Compound Interest",
      definition: "Interest calculated on both the initial principal and the accumulated interest, allowing investments to grow exponentially over time."
    },
    {
      term: "Dollar-Cost Averaging",
      definition: "An investment strategy where a fixed amount is invested at regular intervals regardless of market conditions, reducing the impact of volatility."
    },
    {
      term: "Expense Ratio",
      definition: "The annual fee charged by mutual funds and ETFs, expressed as a percentage of assets, covering management and administrative costs."
    },
    {
      term: "Roth IRA",
      definition: "A retirement account funded with after-tax dollars that grows tax-free and allows tax-free withdrawals in retirement."
    },
    {
      term: "ESG Investing",
      definition: "Investment approach that considers environmental, social, and governance factors alongside financial returns."
    },
    
    // Trading Principles
    {
      term: "Market Order",
      definition: "An order to buy or sell a security immediately at the current market price."
    },
    {
      term: "Limit Order",
      definition: "An order to buy or sell a security at a specific price or better, providing price control but not guaranteeing execution."
    },
    {
      term: "Stop-Loss Order",
      definition: "An order to sell a security when it reaches a specified price, designed to limit an investor's loss on a position."
    },
    {
      term: "Technical Analysis",
      definition: "A method of evaluating securities by analyzing statistics generated by market activity, such as past prices and volume."
    },
    {
      term: "Fundamental Analysis",
      definition: "Evaluation of a security's intrinsic value by examining related economic, financial, and other qualitative and quantitative factors."
    },
    {
      term: "Day Trading",
      definition: "The practice of buying and selling securities within the same trading day, closing all positions before the market closes."
    },
    {
      term: "Swing Trading",
      definition: "A trading style that attempts to capture short to medium-term gains in a stock over a period of days to weeks."
    },
    {
      term: "Risk-to-Reward Ratio",
      definition: "A comparison of the potential profit of a trade to its potential loss, used to evaluate whether a trade is worth taking."
    },
    {
      term: "Volume",
      definition: "The number of shares or contracts traded in a security or market during a given period of time."
    },
    {
      term: "Liquidity",
      definition: "The degree to which an asset can be quickly bought or sold in the market without affecting its price."
    },
    
    // Risk Management
    {
      term: "Systematic Risk",
      definition: "Market risk that affects the entire market or market segment, cannot be eliminated through diversification."
    },
    {
      term: "Unsystematic Risk",
      definition: "Company or industry-specific risk that can be reduced through diversification."
    },
    {
      term: "Position Sizing",
      definition: "The process of determining how much of your portfolio to allocate to a specific investment."
    },
    {
      term: "Trailing Stop",
      definition: "A stop-loss order that moves up as the price of the security increases, helping to lock in profits while limiting downside risk."
    },
    {
      term: "Correlation",
      definition: "A statistical measure of how two securities move in relation to each other, ranging from -1.0 to +1.0."
    },
    {
      term: "Hedging",
      definition: "Taking an offsetting position to reduce risk exposure, similar to buying insurance for your investments."
    },
    {
      term: "Stress Testing",
      definition: "Simulating how a portfolio would perform under various adverse scenarios to identify vulnerabilities."
    },
    
    // Market Analysis
    {
      term: "EPS",
      definition: "Earnings Per Share - a company's net profit divided by the number of outstanding shares, a key indicator of profitability."
    },
    {
      term: "ROE",
      definition: "Return on Equity - a measure of financial performance calculated by dividing net income by shareholder equity."
    },
    {
      term: "Moving Average",
      definition: "A technical indicator that shows the average price of a security over a specified time period, smoothing price data."
    },
    {
      term: "RSI",
      definition: "Relative Strength Index - a momentum oscillator that measures the speed and change of price movements on a scale from 0 to 100."
    },
    {
      term: "MACD",
      definition: "Moving Average Convergence Divergence - a trend-following momentum indicator showing the relationship between two moving averages."
    },
    {
      term: "VIX",
      definition: "Volatility Index - a real-time market index representing the market's expectations for volatility over the coming 30 days."
    },
    {
      term: "Put/Call Ratio",
      definition: "A technical indicator that compares the trading volume of put options to call options, used to gauge market sentiment."
    },
    {
      term: "Yield Curve",
      definition: "A line that plots interest rates of bonds having equal credit quality but differing maturity dates."
    },
    {
      term: "Sector Rotation",
      definition: "The movement of money from one industry sector to another as the economic cycle progresses."
    },
    {
      term: "Quantitative Analysis",
      definition: "The use of mathematical and statistical methods to evaluate investments and market behavior."
    }
  ];

  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          <h1 className="mb-4">📚 Investment Glossary</h1>
          <p className="lead mb-5">
            Essential terms every investor should know
          </p>
          
          {terms.map((item, index) => (
            <div key={index} className="glossary-term">
              <h5>{item.term}</h5>
              <p className="mb-0">{item.definition}</p>
            </div>
          ))}

          <div className="text-center mt-4">
            <Link to="/quiz" className="btn btn-primary-custom">
              Test Your Knowledge →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Glossary;
